import type { Move } from '../../../schemas/move';

/**
 * Archetipi del Mago (manuale V1, rr. 4074-4081).
 *
 * 5 mosse di tipo `class_special`. Pattern razza-tematico standard, con
 * un'eccezione strutturale notevole:
 *
 * - **Umano**: l'archetipo richiede al giocatore di **scegliere un incantesimo
 *   di 1° livello da una qualsiasi Tradizione Sacra del Chierico** da
 *   aggiungere al *Corpus Arcano*. La `choices` strutturata raccoglie i 18
 *   spell L1 unici accessibili al Chierico (13 nuovi del Chierico in
 *   `src/data/spells/chierico.ts` + 5 spell L1 del Mago già condivisi via
 *   `availableTo`: ammaliare-persona, comprensione-dei-linguaggi,
 *   individuazione-del-magico, invisibilità, telepatia). `choiceCount: 1`.
 *   Pattern simmetrico all'archetipo Umano del Chierico (8 spell L1 del Mago).
 */

const archetipoUmano: Move = {
  id: 'mago.archetipo-umano',
  name: 'Archetipo: Umano',
  type: 'class_special',
  classId: 'mago',
  trigger: "Alla creazione del personaggio (alla scelta dell'archetipo)",
  description:
    'Aggiungi al tuo *Corpus Arcano* un incantesimo di primo livello da qualsiasi Tradizione Sacra del Chierico (vedi `choices`).',
  tags: [],
  prerequisites: [],
  replaces: [],
  // 18 opzioni: tutti gli spell L1 unici accessibili al Chierico in qualunque
  // Tradizione (13 nuovi + 5 condivisi col Mago).
  choices: [
    // Spell nuovi del Chierico (13)
    { id: 'chierico.arma-magica', label: 'Arma Magica' },
    { id: 'chierico.benedizione', label: 'Benedizione' },
    { id: 'chierico.controllare-non-morti', label: 'Controllare Non Morti' },
    { id: 'chierico.coraggio', label: 'Coraggio' },
    { id: 'chierico.curare-ferite-leggere', label: 'Curare Ferite Leggere' },
    {
      id: 'chierico.curare-ferite-moderate',
      label: 'Curare Ferite Moderate',
      description: 'Richiede la mossa del Chierico *Primo Soccorso Migliorato* (L6-10).',
    },
    { id: 'chierico.curare-veleni', label: 'Curare Veleni' },
    { id: 'chierico.incutere-paura', label: 'Incutere Paura' },
    { id: 'chierico.individuare-allineamento', label: 'Individuare Allineamento' },
    { id: 'chierico.parlare-con-i-morti', label: 'Parlare con i Morti' },
    { id: 'chierico.santuario', label: 'Santuario' },
    { id: 'chierico.scudo-ultraterreno', label: 'Scudo Ultraterreno' },
    { id: 'chierico.tocco-spettrale', label: 'Tocco Spettrale' },
    // Spell del Mago anche disponibili al Chierico via `availableTo` esteso (5)
    { id: 'mago.ammaliare-persona', label: 'Ammaliare Persona' },
    { id: 'mago.comprensione-dei-linguaggi', label: 'Comprensione dei Linguaggi' },
    { id: 'mago.individuazione-del-magico', label: 'Individuazione del Magico' },
    { id: 'mago.invisibilita', label: 'Invisibilità' },
    { id: 'mago.telepatia', label: 'Telepatia' },
  ],
  choiceCount: 1,
};

const archetipoElfo: Move = {
  id: 'mago.archetipo-elfo',
  name: 'Archetipo: Elfo',
  type: 'class_special',
  classId: 'mago',
  trigger: "Quando lanci l'incantesimo *Individuazione del Magico*",
  description:
    "Per te, l'incantesimo *Individuazione del Magico* costa 0 prese di *Mana* ai fini dei requisiti per lanciarlo e delle conseguenze di un 7-9.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoNano: Move = {
  id: 'mago.archetipo-nano',
  name: 'Archetipo: Nano',
  type: 'class_special',
  classId: 'mago',
  trigger: 'Quando usi *Scudo Magico*',
  description: 'Con un 12+, recuperi la presa di *Mana* che hai speso per attivare la mossa.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoHalfling: Move = {
  id: 'mago.archetipo-halfling',
  name: 'Archetipo: Halfling',
  type: 'class_special',
  classId: 'mago',
  trigger: 'Quando crei illusioni con il trucchetto *Prestidigitazione*',
  description:
    'Le illusioni cui dai vita sono dettagliate e credibili, ma durano comunque poco tempo.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoMezzorco: Move = {
  id: 'mago.archetipo-mezzorco',
  name: "Archetipo: Mezz'orco",
  type: 'class_special',
  classId: 'mago',
  trigger: 'Quando infliggi danni con un incantesimo',
  description:
    "Puoi subire 1d4 danni (*ignora armatura*), e aggiungere il risultato al danno inflitto dall'incantesimo che hai lanciato.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

export const magoArchetypeMoves: readonly Move[] = [
  archetipoUmano,
  archetipoElfo,
  archetipoNano,
  archetipoHalfling,
  archetipoMezzorco,
] as const;
